// ────────────────────────────────────────────────────────────────────────────────
// Nager.Date Public Holiday Service — 100% FREE, no API key, no limits
// https://date.nager.at/
// ────────────────────────────────────────────────────────────────────────────────

export interface PublicHoliday {
  date: string;
  localName: string;
  name: string;
  countryCode: string;
  fixed: boolean;
  global: boolean;
  types: string[];
}

const CACHE_KEY = 'tonveon_holidays_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry { data: PublicHoliday[]; ts: number; year: number; country: string; }

function getCached(year: number, country: string): PublicHoliday[] | null {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY}_${country}_${year}`);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL) return null;
    return entry.data;
  } catch { return null; }
}

function setCache(year: number, country: string, data: PublicHoliday[]) {
  try {
    const entry: CacheEntry = { data, ts: Date.now(), year, country };
    localStorage.setItem(`${CACHE_KEY}_${country}_${year}`, JSON.stringify(entry));
  } catch { /* ignore */ }
}

export async function fetchHolidays(year: number, countryCode: string = 'ES'): Promise<PublicHoliday[]> {
  const cached = getCached(year, countryCode);
  if (cached) return cached;

  try {
    const res = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`);
    if (!res.ok) return [];
    const data: PublicHoliday[] = await res.json();
    setCache(year, countryCode, data);
    return data;
  } catch {
    return [];
  }
}

export function getUpcomingHolidays(holidays: PublicHoliday[], daysAhead: number = 7): PublicHoliday[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const limit = new Date(today);
  limit.setDate(limit.getDate() + daysAhead);

  return holidays.filter(h => {
    const d = new Date(h.date + 'T00:00:00');
    return d >= today && d <= limit;
  });
}

export function isTodayHoliday(holidays: PublicHoliday[]): PublicHoliday | null {
  const today = new Date().toISOString().slice(0, 10);
  return holidays.find(h => h.date === today) || null;
}

export function isDateHoliday(holidays: PublicHoliday[], date: string): PublicHoliday | null {
  return holidays.find(h => h.date === date) || null;
}

export function formatHolidayDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
}
